import ResultList from '../../ResultList/Default';
import Recommender from '../../Recommender/Default';
import EmbeddedRecommender from '../../Recommender/variants/EmbeddedRecommender';
import Spotlight from '../../Spotlight';
import type { ResultsPageProps } from '../types';
import { type FC } from 'react';
import { Flex, Modal } from '@highereducation/hen-ui';
import SingleProgramSubmission from '../../SingleProgramSubmission/Default';
import SingleSubmissionDrawer from '../../SingleProgramSubmission/SingleSubmissionDrawer';
import Loading from '../../Loading';
import { NoResults } from '../../NoResults';
import ResultsBanner from '../../ResultsBanner';
import { SingleSubmissionStep } from '../../SingleProgramSubmission/types';
import { RecommenderStep } from '../../Recommender/types';
import { uuid } from '../../../shared/utils';
import {
  useResultsBannerConfig,
  useResultsListSectionCopy,
} from '../../../hooks/useResultsBannerConfig';
import { ComparisonProvider } from '../../../providers/ComparisonProvider';
import { useResultsPageConfig } from '../../../providers/ResultsPageConfigProvider';
import ComparisonBanner from '../../ComparisonBanner';
import ComparisonModal from '../../ComparisonModal';
import Submissions from '../../Submissions';

const ResultsPageUi: FC<ResultsPageProps> = ({
  /** Results Props */
  resultsAreLoading,
  exactResults,
  relatedResults,
  recommendedResults,
  spotlightResults,
  selectedResult,
  title,
  subtitle,
  /** Flow & Form Props */
  startTime,
  subject,
  category,
  firstName,
  lastName,
  email,
  phone,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  formErrors,
  setEditMode,
  editMode,
  piiLeadErrors,
  /** Popup & Embedded Props */
  onPopupProgramClick,
  onPopupProgramSubmit,
  popupExperienceStep,
  closePopupExperience,
  showPopupExperience,
  showSpotlight,
  goToPopupProgramSelect,
  //Single Program Submission Slideout
  openProgramSlideoutExperience,
  onNextStepsClick,
  singleSubmissionExperienceStep,
  showSlideoutExperience,
  closeSlideoutExperience,
  onSlideoutProgramSubmit,
  goToSlideoutPiiConfirmation,
  // Lead Submission Props
  canSubmitLead,
  isHeclidLoading,
  leadSubmissionStatus,
  setLeadSubmissionStatus,
  // Additional Questions Props
  additionalQuestions,
  onAdditionalQuestionsSubmit,
  lead,
  // Submissions Props
  submittedPrograms,
  submittedResults,
  onViewNextSteps,
  partnerIdentifiers,
}) => {
  const banner = useResultsBannerConfig({ category, firstName });
  const exactSectionCopy = useResultsListSectionCopy({ category, subject });
  const { getComparisonBounds } = useResultsPageConfig();
  const { minItems, maxItems } = getComparisonBounds();

  if (resultsAreLoading) {
    return <Loading />;
  }

  const popupResults = showSpotlight ? spotlightResults : recommendedResults;

  // Render the standalone Spotlight UI on the first popup step when the
  // offerings service returns spotlight matches; the generic Recommender
  // handles all subsequent steps (PII confirmation → submission → exit).
  const showSpotlightUi =
    showSpotlight && popupExperienceStep === RecommenderStep.PROGRAM_SELECT;

  // Handler for popup submit - switches between additional questions and regular submit
  const handlePopupSubmit = (formData?: any) => {
    if (popupExperienceStep === RecommenderStep.ADDITIONAL_QUESTIONS) {
      onAdditionalQuestionsSubmit(formData);
    } else {
      onPopupProgramSubmit();
    }
  };

  // Handler for slideout submit - switches between additional questions and regular submit
  const handleSlideoutSubmit = (formData?: any) => {
    if (
      singleSubmissionExperienceStep ===
      SingleSubmissionStep.ADDITIONAL_QUESTIONS
    ) {
      onAdditionalQuestionsSubmit(formData);
    } else {
      onSlideoutProgramSubmit();
    }
  };

  return (
    <ComparisonProvider minPrograms={minItems} maxPrograms={maxItems}>
      <Flex
        direction='column'
        gap='none'
        width='full'
        className='px-2 md:px-8 max-w-[1536px] mx-auto'
      >
        <ResultsBanner
          header={title || banner.header}
          desktopSubheader={subtitle || banner.desktopSubheader}
          mobileSubheader={subtitle || banner.mobileSubheader}
          listItem1={banner.listItem1}
          listItem2={banner.listItem2}
          listItem3={banner.listItem3}
          imageUrl={banner.imageUrl}
          showImage={banner.showImage}
        />

        <Modal
          open={showPopupExperience}
          onOpenChange={(open) => {
            if (!open) closePopupExperience();
          }}
        >
          <Modal.Content
            size='xl'
            // The Recommender (secondary-50) and Spotlight (neutral-50)
            // inner sections both use rounded-2xl. shadcn DialogContent
            // defaults to rounded-md (~6px), so the smaller outer corner
            // peeks out behind the larger inner corner. Match the inner
            // radius and drop the white bg so the modal edges are flush
            // with whichever inner experience renders.
            className='max-w-5xl p-0 max-h-[95vh] overflow-y-auto rounded-2xl bg-transparent border-0 shadow-none'
          >
            <Modal.Header className='sr-only'>
              <Modal.Title>
                {showSpotlight ? 'Spotlight program' : 'Recommended programs'}
              </Modal.Title>
            </Modal.Header>
            {showSpotlightUi ? (
              <Spotlight
                results={spotlightResults}
                firstName={firstName}
                productClickedCorrelationId={
                  selectedResult?.productClickedCorrelationId
                }
                onProgramClick={onPopupProgramClick}
                onCloseExperience={closePopupExperience}
              />
            ) : (
              <Recommender
                selectedResult={selectedResult}
                canSubmit={canSubmitLead}
                isLoading={isHeclidLoading}
                onBackButtonClick={goToPopupProgramSelect}
                onProgramClick={onPopupProgramClick}
                onProgramSubmit={handlePopupSubmit}
                onCloseExperience={closePopupExperience}
                results={popupResults}
                step={popupExperienceStep}
                firstName={firstName}
                lastName={lastName}
                email={email}
                phone={phone}
                setFirstName={setFirstName}
                setLastName={setLastName}
                setEmail={setEmail}
                setPhone={setPhone}
                formErrors={formErrors}
                setEditMode={setEditMode}
                editMode={editMode}
                piiLeadErrors={piiLeadErrors}
                startTime={startTime}
                setFormErrors={() => {}}
                leadSubmissionStatus={leadSubmissionStatus}
                setLeadSubmissionStatus={setLeadSubmissionStatus}
                questions={additionalQuestions}
                productClickedCorrelationId={
                  selectedResult?.productClickedCorrelationId
                }
                submittedResults={submittedResults}
                partnerIdentifiers={partnerIdentifiers}
              />
            )}
          </Modal.Content>
        </Modal>

        <SingleSubmissionDrawer
          open={showSlideoutExperience}
          onClose={closeSlideoutExperience}
        >
          {selectedResult && (
            <SingleProgramSubmission
              onCloseExperience={closeSlideoutExperience}
              selectedResult={selectedResult}
              step={singleSubmissionExperienceStep}
              startTime={startTime}
              piiLeadErrors={piiLeadErrors}
              setEditMode={setEditMode}
              editMode={editMode}
              setFirstName={setFirstName}
              setLastName={setLastName}
              setEmail={setEmail}
              setPhone={setPhone}
              formErrors={[]}
              setFormErrors={() => {}}
              formHeading='Confirm your contact details:'
              formSubHeading={''}
              onBackButtonClick={goToSlideoutPiiConfirmation}
              onProgramSubmit={handleSlideoutSubmit}
              firstName={firstName}
              lastName={lastName}
              email={email}
              phone={phone}
              leadSubmissionStatus={leadSubmissionStatus}
              setLeadSubmissionStatus={setLeadSubmissionStatus}
              canSubmit={canSubmitLead}
              isLoading={isHeclidLoading}
              questions={additionalQuestions}
              productClickedCorrelationId={
                selectedResult?.productClickedCorrelationId || uuid()
              }
              partnerIdentifiers={partnerIdentifiers}
            />
          )}
        </SingleSubmissionDrawer>
      </Flex>

      {/* Submissions renders its own full-width Section with an inner
          max-w-[1536px] wrapper, so it must sit outside the constrained
          container above -- otherwise the section's bg-primary-100
          background gets clipped to 1536px instead of bleeding
          edge-to-edge. The component returns null when there are no
          submissions, so it is safe to render unconditionally. */}
      <Submissions
        submissions={submittedPrograms}
        firstName={firstName}
        onViewNextSteps={onViewNextSteps}
      />

      <Flex
        direction='column'
        gap='none'
        width='full'
        className='px-2 md:px-8 max-w-[1536px] mx-auto'
      >
        {recommendedResults && (
          <EmbeddedRecommender
            results={recommendedResults}
            subject={subject}
            onProgramClick={openProgramSlideoutExperience}
            submittedResults={submittedResults}
            showPopupExperience={showPopupExperience}
            lead={lead}
            partnerIdentifiers={partnerIdentifiers}
          />
        )}

        {!!exactResults?.length && (
          <ResultList
            header={exactSectionCopy.header}
            subheader={exactSectionCopy.subheader}
            onProgramClick={openProgramSlideoutExperience}
            onNextStepsClick={onNextStepsClick}
            results={exactResults}
            submittedResults={submittedResults}
            lead={lead}
            partnerIdentifiers={partnerIdentifiers}
          />
        )}

        {!exactResults?.length &&
          !recommendedResults?.length &&
          !relatedResults?.length && <NoResults />}

        {!!relatedResults?.length && (
          <ResultList
            onProgramClick={openProgramSlideoutExperience}
            onNextStepsClick={onNextStepsClick}
            results={relatedResults}
            header='Other School Programs You Might Like'
            subheader={`These schools offer similar programs to ${subject}.`}
            submittedResults={submittedResults}
            lead={lead}
            partnerIdentifiers={partnerIdentifiers}
          />
        )}
      </Flex>

      {/* Comparison stack -- ported from the legacy Grad variant.
          ComparisonProvider above gates context (and owns TCPA detail
          hydration); banner and modal render the floating compare-tray
          and the side-by-side modal. */}
      <ComparisonBanner />
      <ComparisonModal
        squareButtons
        onProductClick={openProgramSlideoutExperience}
        submittedResults={submittedResults}
      />
    </ComparisonProvider>
  );
};

export default ResultsPageUi;
